// A figure with an eye button beside it: hidden (dots) until someone asks to see it, and hidden again on a second
// click. For money on a screen other people can see: QOR's balances at a processor, totals, a card's limits.
import { useState } from 'react';

// Material "visibility" / "visibility_off"
const EYE =
  'M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z';
const EYE_OFF =
  'M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z';

export const HIDDEN = '••••••';

/**
 * `label` names the figure for the button ("Show EUR balance"). `shown` and `onToggle` let a page drive several at
 * once (Show all); left out, the figure keeps its own state and starts hidden.
 */
export function HiddenNumber(props: { value: string; label: string; shown?: boolean; onToggle?: () => void }) {
  const [own, setOwn] = useState(false);
  const shown = props.shown ?? own;
  const action = `${shown ? 'Hide' : 'Show'} ${props.label}`;
  return (
    <span className="qc-hidden-number">
      <span data-testid={`number-${props.label}`}>{shown ? props.value : HIDDEN}</span>
      <button
        type="button"
        className="qc-eye"
        aria-label={action}
        aria-pressed={shown}
        title={action}
        onClick={props.onToggle ?? (() => setOwn((now) => !now))}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path d={shown ? EYE_OFF : EYE} fill="currentColor" />
        </svg>
      </button>
    </span>
  );
}

/** The figures of one page, by label: each with its own eye, and one switch for all of them. */
export function useShownNumbers() {
  const [shown, setShown] = useState<ReadonlySet<string>>(new Set());
  return {
    props: (label: string) => ({
      label,
      shown: shown.has(label),
      onToggle: () =>
        setShown((now) => {
          const next = new Set(now);
          if (!next.delete(label)) next.add(label);
          return next;
        }),
    }),
    anyShown: shown.size > 0,
    showAll: (labels: string[]) => setShown(new Set(labels)),
    hideAll: () => setShown(new Set()),
  };
}
